import { Alert, Card, Flex } from 'antd';
import { Subtitle, Text, Title } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { Patient } from 'lib/documents';
import { pluralize } from 'lib/utils/string/normalise';
import { usePatientInfoContext } from '../../../patient/_utils/patient-info/hook-context-patientinfo';
import { ScheduleTestUI } from './_components/display-schedule-test';
import { UserKnowledgePanel } from './_components/panel-user-knowledge';

/**
 * Clinician-only page for managing user assessments
 *
 * Users can
 * 1. Create an assessment link
 * 2. See the status of an assessment link
 * 3. Copy an assessment link
 *
 * User knowledge transfer UX
 * 1. What is this page? "This page allows you to manage the assessments of your user. On this page you can see whether or not your Test-Taker has started their assessment"
 * 2. What steps do I need to take? "First you generate an assessment link. Then you send it to your Test-Taker"
 * 3. What steps does my Test-Taker need to take?
 */
export function PageScheduleTest() {
  const patientInfo = usePatientInfoContext();
  return (
    <PrintableRootPage>
      <Flex gap={16} vertical>
        <DisplayFriendlyPatientIdentifier
          patient={patientInfo.patientInfo.patient}
          suffix="assessment invites"
        />
        <Card>
          <UserKnowledgePanel />
        </Card>
        <Card>
          <Flex gap={16} vertical>
            <Alert
              type="info"
              message={
                <div>
                  <Subtitle>NOTICE</Subtitle>
                  <Text>
                    Assessment Links are sensitive credentials. Anyone with this
                    link will be able to complete a test on behalf of the
                    Test-Taker. These links must only be shared with the
                    intended recipient Test-Taker.
                  </Text>
                </div>
              }
              showIcon
            />
            <ScheduleTestUI />
          </Flex>
        </Card>
      </Flex>
    </PrintableRootPage>
  );
}

export function DisplayFriendlyPatientIdentifier(props: {
  patient: Patient;
  suffix: string;
  pluralize?: boolean;
}) {
  if (!props.patient.displayName || props.patient.displayName.length === 0) {
    return (
      <div>
        <Title>Test-Taker {props.patient.id}</Title>
        <Alert
          type="info"
          message={
            'You are seeing an ID because the Test-Taker has not set up their ConcussionRx account. When they do you will see their name.'
          }
          showIcon
        />
      </div>
    );
  }
  return (
    <div>
      <Title>
        {props.pluralize
          ? pluralize(props.patient.displayName)
          : props.patient.displayName}{' '}
        {props.suffix}
      </Title>
    </div>
  );
}
